import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { changeProps } from "find-and";
import { BP_PHONE_PARAMS } from "../constants/businessPlan";
import { useAppInitialized } from "./app/useAppInitialized";
import { useUser } from "./auth/useUser";
import { useCurrentCity } from "./app/useCurrentCity";
import { useCityList } from "./dict/useCityList";
import { useSubjectList } from "./dict/useSubjectList";
import { useRetailBusinessItems } from "./dict/retail/useRetailBusinessItems";
import { useRetailBusinessPriceRange } from "./dict/retail/useRetailBusinessPriceRange";
import { useSelfBusinessItems } from "./dict/self/useSelfBusinessItems";
import { useSection } from "./app/useSection";
import { usePromBusinessItems } from "./dict/prom/usePromBusinessItems";
import { useProfileOptions } from "./bp/useProfileOptions";
import { useSelectedProfileId } from "./bp/useSelectedProfileId";
import { useDefaultProfileId } from "./bp/useDefaultProfileId";
import { getUserInfoOptions, isValidPhone, simplifyPhone } from "../utils";
import { api } from "../api";
import { AppSection } from "../slices/app/types";
export const usePageLoad = () => {
    const [appInitialized] = useAppInitialized();
    const [searchParams] = useSearchParams();
    const [user] = useUser();
    const [section] = useSection();
    const [cityList, setCityList] = useCityList();
    const [subjectList, setSubjectList] = useSubjectList();
    const [currentCity] = useCurrentCity();
    const bpId = searchParams.get("bp_id");
    const [retailBusinessItems, setRetailBusinessItems] = useRetailBusinessItems();
    const [retailBusinessPriceRange, setRetailBusinessPriceRange] = useRetailBusinessPriceRange();
    const [selfBusinessItems, setSelfBusinessItems] = useSelfBusinessItems();
    const [promBusinessItems, setPromBusinessItems] = usePromBusinessItems();
    const [, setProfileOptions] = useProfileOptions();
    const [, setSelectedProfileId] = useSelectedProfileId();
    const [, setDefaultProfileId] = useDefaultProfileId();
    useEffect(() => {
        if (!appInitialized) {
            return;
        }
        if (cityList === undefined) {
            api.city.getCityList(user.guid).then(setCityList);
        }
        if (subjectList === undefined) {
            api.city.getSubjectList(user.guid).then(setSubjectList);
        }
    }, [appInitialized]); // eslint-disable-line
    useEffect(() => {
        if (!appInitialized || currentCity === undefined) {
            return;
        }
        if ([AppSection.Retail, AppSection.RetailIntegral].includes(section)) {
            if (retailBusinessPriceRange === undefined ||
                !retailBusinessPriceRange.every(range => range.city === currentCity.id)) {
                api.business.getBusinessPriceRange(currentCity.id, user.guid).then(setRetailBusinessPriceRange);
            }
            if (retailBusinessItems === undefined) {
                api.business.getBusiness(user.guid).then(setRetailBusinessItems);
            }
        }
        if (section === AppSection.SelfEmployed) {
            if (selfBusinessItems === undefined) {
                api.business.getSelfEmployedBusiness(user.guid).then(setSelfBusinessItems);
            }
        }
        if ([AppSection.Enterprise, AppSection.EnterpriseIntegral].includes(section)) {
            if (promBusinessItems === undefined) {
                api.business.getPromEmployedBusiness(user.guid).then(setPromBusinessItems);
            }
        }
    }, [currentCity, section, appInitialized]); // eslint-disable-line
    useEffect(() => {
        if (!section) {
            return;
        }
        const initialProfileOptions = getUserInfoOptions(user?.user_info).map(item => ({
            value: item.id,
            label: item.title,
            description: item.description,
            isDefault: item.isDefault,
            isBusiness: item.isBusiness,
            isSelf: item.isSelf,
            values: item.values,
            initialValues: item.initialValues,
        }));
        const getFilteredProfileOptions = () => {
            switch (section) {
                case AppSection.Retail:
                    return initialProfileOptions.filter(item => !item.isSelf);
                case AppSection.SelfEmployed:
                    return initialProfileOptions.filter(item => !item.isBusiness);
                default:
                    return initialProfileOptions;
            }
        };
        const filteredOptions = getFilteredProfileOptions();
        if (bpId) {
            (async () => {
                const savedBpParams = section === AppSection.Retail
                    ? await api.businessPlan.getSavedRetailBusinessPlanParams(bpId)
                    : await api.businessPlan.getSavedSelfBusinessPlanParams(bpId);
                if (savedBpParams?.parameters?.user_inn) {
                    const savedOption = filteredOptions.find(item => item.values.user_inn === savedBpParams?.parameters?.user_inn && item.values.user_add_flag === savedBpParams?.parameters?.user_add_flag);
                    if (savedOption) {
                        setProfileOptions(changeProps(filteredOptions, { value: savedOption.value }, {
                            values: Object.keys(savedOption.values).reduce((acc, curr) => ({
                                ...acc,
                                [curr]: BP_PHONE_PARAMS.includes(curr) && isValidPhone(savedBpParams.parameters?.[curr])
                                    ? simplifyPhone(savedBpParams.parameters?.[curr])
                                    : savedBpParams.parameters?.[curr],
                            }), {}),
                        }));
                        setSelectedProfileId(savedOption.value);
                        return;
                    }
                }
                setProfileOptions(filteredOptions);
                setSelectedProfileId(filteredOptions.find(item => item.isDefault)?.value || filteredOptions[0]?.value);
            })();
        }
        else {
            setProfileOptions(filteredOptions);
            setSelectedProfileId(filteredOptions.find(item => item.isDefault)?.value || filteredOptions[0]?.value);
        }
        setDefaultProfileId(filteredOptions.find(item => item.isDefault)?.value || filteredOptions[0]?.value);
    }, [user, section, bpId]); // eslint-disable-line
};
